/**
 * InlinePlanModal — opens the plan picker (PlanSelector → checkout/confirm)
 * in place, anywhere a CTA wants to upgrade a product without navigating to
 * the billing page. Resolves the product's row + catalog entry from the
 * caller's usePricing() result, then delegates to PlanChangeFlow.
 *
 * The full billing page (/account/billing?product=<id>) remains the place to
 * MANAGE a plan; this is the fast in-place path to CHANGE/BUY one.
 */
import React from "react";
import type { PricingHook } from "@d-m-organization/pricing-ui";
import { deriveBillingView } from "../lib/deriveBillingView.js";
import { PlanChangeFlow, type PlanChangeIntent } from "./PlanChangeFlow.js";

export type InlinePlanModalProps = {
  readonly pricing: PricingHook;
  readonly productId: string;
  readonly intent: PlanChangeIntent;
  readonly initialTier?: "pro" | "business";
  readonly onClose: () => void;
};

export function InlinePlanModal({ pricing, productId, intent, initialTier, onClose }: InlinePlanModalProps) {
  const rows = deriveBillingView(pricing.catalog, pricing.subscription);
  const row = rows.find((r) => r.productId === productId);
  const product = pricing.catalog?.products.find((p) => p.productId === productId);
  if (!row || !product) return null;

  return (
    <PlanChangeFlow
      row={row}
      product={product}
      intent={intent}
      initialTier={initialTier}
      previewUpgrade={pricing.previewUpgrade}
      upgrade={pricing.upgrade}
      checkout={pricing.checkout}
      onClose={onClose}
      onApplied={() => { onClose(); void pricing.refetch(); }}
    />
  );
}
